/**
 * House of Morka product page (concept F). Owns variant selection (Fit × Colour × Size),
 * gallery, photo preview, mix & match slots, add-ons, dates, summary and the mobile sticky bar.
 * Adding to cart is left to Atelier's <product-form-component>; bundle slots and add-ons are
 * added through the Cart API right before the main item is submitted.
 */

const SIZE_RANK = { XS: 0, S: 1, M: 2, L: 3, XL: 4, '2XL': 5, XXL: 5, '3XL': 6 };
const sizeRank = (s) => (s in SIZE_RANK ? SIZE_RANK[s] : 100 + (parseInt(s, 10) || 0));
// Women M (was S, 2026-10-18): every default falls in the same print box as the catalogue mockup size (DESIGN.md §7),
// so the preview, the cm label and the Print size property agree before the shopper taps a size.
const DEFAULT_SIZE = { Men: 'M', Women: 'M', Kids: '8 (134 cm)', Crew: 'M', Hoodie: 'M' };
// Shopify option values stay Men / Women / Kids; the buttons say Him / Her / Kid (ARVI 2026-10-16).
// Sweatshirt / Hoodie (430 / 431, unisex) are values of the same Fit option (ARVI 2026-10-17). The Shopify value is
// 'Sweatshirt' (renamed from 'Crew' 2026-10-20); the section maps it to the internal key Crew.
const FIT_LABEL = { Men: 'Him', Women: 'Her', Kids: 'Kid', Crew: 'Sweatshirt', Hoodie: 'Hoodie' };
const FIT_CUT = { Men: "men's fit", Women: "women's fit", Kids: "kids' fit", Crew: 'unisex sweatshirt', Hoodie: 'hoodie' };
/** gallery image → which cut it shows ('' = for every cut: worn by the owner, laid flat) */
const mediaFit = (alt) => {
  const m = /– (men|women|kids|crew|hoodie) /i.exec(alt || '');
  return m ? { men: 'Men', women: 'Women', kids: 'Kids', crew: 'Crew', hoodie: 'Hoodie' }[m[1].toLowerCase()] : '';
};
const mediaColour = (alt) => (/ black /i.test(alt || '') ? 'Black' : / white /i.test(alt || '') ? 'White' : '');
/** Blank garment packshots (theme assets hom-blank-<code>-<colour>-<side>.jpg) – the back view of the gallery */
const BLANK_CODE = { Men: '813', Women: '814', Kids: '815', Crew: '430', Hoodie: '431' };

class HomProduct extends HTMLElement {
  #ac = new AbortController();
  #variants = [];
  #media = [];
  #state = { fit: 'Men', fitChosen: false, colour: 'White', size: 'M', sizeChosen: false, bundle: 1, img: 0, view: 'front', photo: 'now' };
  #slots = [
    { fit: 'Women', what: 'same', size: 'S' },
    { fit: 'Kids', what: 'other', size: '8 (134 cm)' },
  ];
  #prepared = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.#media = JSON.parse(this.querySelector('[data-media]')?.textContent || '[]');

    const current = Number(this.querySelector('[data-variant-id]')?.value);
    const cv = this.#variants.find((v) => v.id === current) || this.#variants[0];
    if (cv) this.#state = { ...this.#state, fit: cv.fit, colour: cv.colour, size: cv.size };
    // 2026-10-18 (ARVI): open on the cut and colour of the product's photo (Her for a woman, Kid for a child) –
    // the shopper can still switch. A ?variant= link keeps its own variant.
    if (cv && this.dataset.variantChosen === 'true') {
      this.#state = { ...this.#state, fitChosen: true, sizeChosen: true };
    } else if (cv) {
      const df = this.dataset.defaultFit || '';
      const fit = df && this.#variants.some((v) => v.fit === df) ? df : cv.fit;
      const dc = this.dataset.defaultColour || '';
      const colour = dc && this.#variants.some((v) => v.fit === fit && v.colour === dc && v.available !== false) ? dc : this.#variants.find((v) => v.fit === fit)?.colour || cv.colour;
      this.#state = { ...this.#state, fit, colour, fitChosen: true };
      const sizes = this.#sizesFor(fit);
      this.#state.size = sizes.includes(DEFAULT_SIZE[fit]) ? DEFAULT_SIZE[fit] : sizes[0];
    }
    // a link may ask for a cut (?fit=men|women|kids|crew|hoodie)
    const askedFit = { men: 'Men', him: 'Men', women: 'Women', her: 'Women', kids: 'Kids', kid: 'Kids', crew: 'Crew', sweatshirt: 'Crew', hoodie: 'Hoodie' }[(new URLSearchParams(window.location.search).get('fit') ?? '').toLowerCase()];
    if (askedFit && this.#variants.some((v) => v.fit === askedFit)) {
      const sizes = [...new Set(this.#variants.filter((v) => v.fit === askedFit).map((v) => v.size))];
      this.#state = { ...this.#state, fit: askedFit, fitChosen: true, size: sizes.includes(DEFAULT_SIZE[askedFit]) ? DEFAULT_SIZE[askedFit] : sizes[0] };
    }
    // the home page hero passes the tee colour it showed (?color=white|black)
    const askedColour = (new URLSearchParams(window.location.search).get('color') ?? '').toLowerCase();
    const wanted = askedColour === 'black' ? 'Black' : askedColour === 'white' ? 'White' : '';
    if (wanted && wanted !== this.#state.colour && this.#variants.some((v) => v.fit === this.#state.fit && v.colour === wanted && v.available !== false)) {
      this.#state.colour = wanted;
    }
    this.#state.img = this.#frontIndex();

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    const form = this.#form;
    // Atelier's product form listens on document in the capture phase, so a listener on the form ran too late: an
    // empty form (no photo / name) or a 2–3 item set still added the main item (2026-10-18). Window capture runs first.
    window.addEventListener('submit', (e) => e.target === this.#form && this.#onSubmit(e), { signal, capture: true });
    // the studio popup owns photo, name, he/she and caption in the popup flow; copy them back when it closes
    this.querySelector('[data-sk-dialog]')?.addEventListener('close', this.#onStudioClose, { signal });
    document.addEventListener('cart:error', this.#onCartError, { signal });
    // Atelier's product form fires the standard cart events; the CTA says "Adding…" until the line lands
    document.addEventListener('shopify:cart:error', this.#onCartError, { signal });
    document.addEventListener('shopify:cart:lines-update', this.#onLinesUpdate, { signal });

    this.#applyStyleParam();
    this.#renderDates();
    this.#render();
    this.#setupSticky();
  }

  disconnectedCallback() {
    this.#ac.abort();
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('[data-hom-form]'));
  }

  get #currency() {
    return this.dataset.currency || '€';
  }

  /** @param {number} cents */
  #money(cents) {
    const v = Math.round(cents) / 100;
    return this.#currency + (Number.isInteger(v) ? String(v) : v.toFixed(2));
  }

  #q(sel) {
    return this.querySelector(sel);
  }

  // ---------- events ----------

  /** @param {Event} e */
  #onClick = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const b = t.closest('button, [data-thumb]');
    if (!(b instanceof HTMLElement) || !this.contains(b)) return;

    if (b.dataset.photoMode) return this.#setPhotoMode(b.dataset.photoMode);
    if (b.dataset.fit) {
      this.#q('[data-fit-group]')?.closest('.step')?.classList.remove('needs-choice');
      const ask = this.#q('[data-fit-ask]');
      if (ask instanceof HTMLElement) ask.hidden = true;
      return this.#set({ fit: b.dataset.fit, fitChosen: true });
    }
    if (b.dataset.colour && !b.hasAttribute('disabled')) return this.#set({ colour: b.dataset.colour });
    if (b.dataset.size && b.closest('[data-size-group]')) return this.#set({ size: b.dataset.size, sizeChosen: true });
    if (b.dataset.bundle) return this.#set({ bundle: Number(b.dataset.bundle) });
    if (b.dataset.thumb !== undefined) return this.#set({ view: b.dataset.thumb });
    if (b.dataset.slotFit) {
      const i = Number(b.closest('[data-slot]')?.getAttribute('data-slot'));
      const slot = this.#slots[i];
      slot.fit = b.dataset.slotFit;
      slot.size = DEFAULT_SIZE[slot.fit] || slot.size;
      return this.#render();
    }
    if (b.dataset.slotWhat) {
      const i = Number(b.closest('[data-slot]')?.getAttribute('data-slot'));
      this.#slots[i].what = b.dataset.slotWhat;
      return this.#render();
    }
    if (b.dataset.slotSize) {
      const i = Number(b.closest('[data-slot]')?.getAttribute('data-slot'));
      this.#slots[i].size = b.dataset.slotSize;
      return this.#render();
    }
    if (b.hasAttribute('data-open-size')) {
      e.preventDefault();
      const d = this.#q('[data-size-dialog]');
      if (d instanceof HTMLDialogElement) d.showModal();
      return;
    }
    if (b.hasAttribute('data-close-size')) {
      const d = this.#q('[data-size-dialog]');
      if (d instanceof HTMLDialogElement) d.close();
      return;
    }
    if (b.dataset.xsell) {
      this.#slots[0] = { fit: b.dataset.xsell, what: 'same', size: DEFAULT_SIZE[b.dataset.xsell] || 'M' };
      this.#set({ bundle: Math.max(2, this.#state.bundle) });
      this.#q('[data-bundle-group]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (b.hasAttribute('data-sticky-cta')) {
      this.#form?.requestSubmit();
    }
  };

  /** @param {Event} e */
  #onInput = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.hasAttribute('data-caption') || t.hasAttribute('data-name')) this.#renderSummary();
  };

  get #popup() {
    return this.dataset.flow === 'popup';
  }

  /** The studio dialog closed: he/she and the caption come back into the order (the name and photo sync live). */
  #onStudioClose = () => {
    const dlg = this.#q('[data-sk-dialog]');
    if (!dlg) return;
    const sex = dlg.querySelector('[data-sk-sexv][aria-pressed="true"]')?.getAttribute('data-sk-sexv');
    if (sex) for (const r of this.querySelectorAll('[data-sex]')) if (r instanceof HTMLInputElement) r.checked = r.value === sex;
    const capIn = dlg.querySelector('[data-sk-capinput]');
    const cap = this.#q('[data-caption]');
    if (capIn instanceof HTMLInputElement && cap instanceof HTMLInputElement && !capIn.hidden) cap.value = capIn.value.trim();
    const nameIn = dlg.querySelector('[data-sk-nameinput]');
    const name = this.#q('[data-name]');
    if (nameIn instanceof HTMLInputElement && name instanceof HTMLInputElement && nameIn.value.trim() && !name.value.trim()) name.value = nameIn.value.trim();
    const need = dlg.querySelector('[data-sk-need]');
    if (need instanceof HTMLElement) need.hidden = true;
    this.#showDesignNote();
    this.#renderSummary();
  };

  /** What the design still lacks for the cart (popup flow): '' when the photo (or "email it later") and the name are there. */
  #designMissing() {
    if (!this.#popup) return '';
    const name = /** @type {HTMLInputElement | null} */ (this.#q('[data-name]'))?.value.trim();
    const file = /** @type {HTMLInputElement | null} */ (this.#q('[data-photo-input]'));
    const pet = this.dataset.pet || 'pet';
    const noPhoto = this.#state.photo === 'now' && file instanceof HTMLInputElement && !file.files?.length;
    if (noPhoto && !name) return `Add a photo of your ${pet} and the name – then it goes to the cart.`;
    if (noPhoto) return `Add a photo of your ${pet} – then it goes to the cart.`;
    return name ? '' : 'Write the name – it goes on the design.';
  }

  /**
   * Add to cart before the design is made (ARVI 2026-10-18: say why instead of a silent stop):
   * the studio opens with "First, make your design" on top, and the same line stays under the button until it is done.
   */
  #askStudio(msg) {
    this.#needDesign = true;
    const open = this.#q('[data-pj-cta]');
    if (open instanceof HTMLElement) open.click();
    const need = this.#q('[data-sk-dialog] [data-sk-need]');
    if (need instanceof HTMLElement) {
      const m = need.querySelector('[data-sk-need-msg]');
      if (m) m.textContent = msg;
      need.hidden = false;
      need.scrollIntoView({ block: 'nearest' });
    } else {
      this.#showDesignNote();
    }
  }

  #needDesign = false;

  /** The line under Add to cart: shown after a blocked add, gone once the design is complete. */
  #showDesignNote() {
    const el = this.#q('[data-form-error]');
    if (!(el instanceof HTMLElement)) return;
    const missing = this.#needDesign ? this.#designMissing() : '';
    if (missing) {
      el.textContent = `First, make your design: ${missing.charAt(0).toLowerCase()}${missing.slice(1)}`;
      el.hidden = false;
      el.dataset.design = '1';
    } else if (el.dataset.design) {
      el.hidden = true;
      delete el.dataset.design;
      this.#needDesign = false;
    }
  }

  /** @param {Event} e */
  #onChange = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.hasAttribute('data-photo-input')) return this.#onPhotoChange(t);
    if (t.hasAttribute('data-photo-later-check')) {
      this.#setPhotoMode(t.checked ? 'later' : 'now');
      const n = this.#q('[data-pj-later-note]');
      if (n instanceof HTMLElement) n.hidden = !t.checked;
      return this.#renderSummary();
    }
    if (t.hasAttribute('data-pet2-input')) {
      const nm = this.#q('[data-pet2-name]');
      if (nm) nm.textContent = t.files?.[0]?.name || "2nd pet's photo";
      return;
    }
    if (t.hasAttribute('data-addon-more')) {
      // second pet: show the photo + "custom work" note, and only send its properties when it is ticked
      const panel = this.#q(`[data-addon-panel="${t.getAttribute('data-addon-more')}"]`);
      if (panel instanceof HTMLElement) {
        panel.hidden = !t.checked;
        for (const f of panel.querySelectorAll('input')) f.disabled = !t.checked;
      }
    }
    if (t.hasAttribute('data-sex') || t.hasAttribute('data-addon')) this.#renderSummary();
  };

  #onCartError = () => {
    this.#prepared = false;
    this.#setBusy(false);
    this.#adding(false);
  };

  /** @param {Event & { promise?: Promise<unknown> }} e */
  #onLinesUpdate = async (e) => {
    // Atelier dispatches this before the form's submit listeners run, so #addingNow is checked after the wait
    try {
      await e.promise;
    } catch {
      /* the error event resets the button */
    }
    if (this.#addingNow) this.#adding(false);
  };

  /** Bundle slots and add-ons go into the cart first, then the main item through Atelier's form. */
  #onSubmit = async (e) => {
    const form = this.#form;
    if (!form) return;
    if (this.#popup && !this.#prepared) {
      const missing = this.#designMissing();
      if (missing) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.#askStudio(missing);
        return;
      }
    }
    if (!this.#state.fitChosen) {   // Him / Her / Kid / Sweatshirt / Hoodie must be picked by hand
      e.preventDefault();
      e.stopImmediatePropagation();
      this.#askFit();
      return;
    }
    if (this.#prepared) {
      this.#prepared = false;
      return; // let product-form-component handle it
    }
    const extras = this.#extraLines();
    if (extras.length === 0) {
      this.#adding(true);
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    if (!form.reportValidity()) return;

    this.#setBusy(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: extras }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Could not add the extra items');
      }
      this.#prepared = true;
      this.#adding(true);
      form.requestSubmit();
      setTimeout(() => this.#setBusy(false), 1500);
    } catch (err) {
      this.#setBusy(false);
      this.#showError(err instanceof Error ? err.message : 'Could not add the extra items');
    }
  };

  #askFit() {
    const group = this.#q('[data-fit-group]');
    const step = group?.closest('.step');
    const ask = this.#q('[data-fit-ask]');
    if (ask instanceof HTMLElement) ask.hidden = false;
    if (step instanceof HTMLElement) {
      step.classList.remove('needs-choice');
      void step.offsetWidth;   // restart the pop animation
      step.classList.add('needs-choice');
      step.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const first = group?.querySelector('button');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  /** product photo of the chosen cut × colour (each design has one per Fit × Colour) */
  #frontIndex() {
    const st = this.#state;
    const v = this.#variant() || this.#variants.find((x) => x.fit === st.fit && x.colour === st.colour);
    let i = this.#media.findIndex((m) => m.id === v?.media);
    if (i < 0) i = this.#media.findIndex((m) => mediaFit(m.alt) === st.fit && mediaColour(m.alt) === st.colour);
    return i >= 0 ? i : 0;
  }

  /**
   * Gallery = 4 pictures (ARVI 2026-10-18): front and back of the chosen cut in the chosen colour, the owner wearing it
   * next to the pet, and the tee laid flat on a neutral surface (always, right after the owner – ARVI 2026-10-18 evening).
   * The front/back are drawn live by hom-position.js (blank + print where it goes).
   */
  #slides() {
    const st = this.#state;
    const front = this.#media[this.#frontIndex()];
    const tpl = this.dataset.blankTpl || '';
    const back = tpl ? tpl.replace('{code}', BLANK_CODE[st.fit] || '813').replace('{colour}', st.colour.toLowerCase()).replace('{side}', 'back') : '';
    let life = this.#media.find((m) => /worn|owner/i.test(m.alt));
    if (!life) life = this.#media.find((m) => !mediaFit(m.alt) && !/flat/i.test(m.alt));
    const flat = this.#media.find((m) => /laid flat|flat lay/i.test(m.alt));
    const label = `${FIT_CUT[st.fit] || st.fit}, ${st.colour.toLowerCase()}`;
    return [
      front && { view: 'front', src: front.src, thumb: front.thumb, alt: front.alt, label: `Front · ${label}` },
      back && { view: 'back', src: back, thumb: back, alt: `Back of the ${label}`, label: `Back · ${label}` },
      life && { view: 'life', src: life.src, thumb: life.thumb, alt: life.alt, label: 'Worn, with the pet' },
      flat && { view: 'flat', src: flat.src, thumb: flat.thumb, alt: flat.alt, label: 'Laid flat' },
    ].filter(Boolean);
  }

  // ---------- state ----------

  #set(patch) {
    this.#state = { ...this.#state, ...patch };
    if ('fit' in patch) {
      const sizes = this.#sizesFor(this.#state.fit);
      // the shopper's own size choice survives a change of cut; the preset one follows the cut's usual size
      if (!this.#state.sizeChosen || !sizes.includes(this.#state.size)) this.#state.size = sizes.includes(DEFAULT_SIZE[this.#state.fit]) ? DEFAULT_SIZE[this.#state.fit] : sizes[0];
    }
    // 2026-10-15: kiekvienas dizainas turi nuotrauką kiekvienam Fit × Colour – rodoma pakeitus bet kurį iš jų
    if ('fit' in patch || 'colour' in patch) {
      this.#state.img = this.#frontIndex();
      if (this.#state.view === 'life' || this.#state.view === 'flat') this.#state.view = 'front';
    }
    this.#render();
  }

  #sizesFor(fit) {
    const set = new Set(this.#variants.filter((v) => v.fit === fit && v.colour === this.#state.colour).map((v) => v.size));
    return [...set].sort((a, b) => sizeRank(a) - sizeRank(b));
  }

  /** Tees (Edge 813/814) run a size small – ARVI 2026-10-22: "I wear M, I'd order L". Men: usual size → size up;
   *  women: slim, fitted cut. Kids / sweatshirt / hoodie: no tip. */
  #sizeTip(fit, size, sizes) {
    if (fit !== 'Men' && fit !== 'Women') return '';
    const i = sizes.indexOf(size);
    const prev = i > 0 ? sizes[i - 1] : '';
    const next = i >= 0 && i < sizes.length - 1 ? sizes[i + 1] : '';
    // ARVI 2026-10-22: one plain pattern for every size, no "snug" / "runs small" jargon
    const cut = fit === 'Women' ? 'Fitted cut. ' : '';
    if (prev && next) return `${cut}${size} here = your usual ${prev}. Most people who wear ${size} order ${next}.`;
    if (next) return `${cut}${size} here = smaller than your usual ${size}. Most people who wear ${size} order ${next}.`;
    if (prev) return `${cut}${size} here = your usual ${prev}. Most people who wear ${prev} order ${size}.`;
    return '';
  }

  #variant(fit = this.#state.fit, size = this.#state.size, colour = this.#state.colour) {
    return this.#variants.find((v) => v.fit === fit && v.colour === colour && v.size === size) || null;
  }

  #unitPrice(fit) {
    const v = this.#variants.find((x) => x.fit === fit && x.colour === this.#state.colour) || this.#variants.find((x) => x.fit === fit);
    return v ? v.price : 0;
  }

  /** Items 2…n – page A only. Page B's bundle tiles are information only (ARVI 2026-10-18), so its bundle stays 1. */
  #bundleSlots() {
    return this.#slots.slice(0, this.#state.bundle - 1);
  }

  /** @param {number} n */
  #discount(n) {
    if (n < 2) return 0;
    return Number(this.getAttribute(n >= 3 ? 'data-discount-3' : 'data-discount-2')) || 0;
  }

  #extraLines() {
    const lines = [];
    const n = this.#state.bundle;
    const name = /** @type {HTMLInputElement | null} */ (this.#q('[data-name]'))?.value.trim() || '';
    const sex = /** @type {HTMLInputElement | null} */ (this.#q('[data-sex]:checked'))?.value || '';
    const caption = /** @type {HTMLInputElement | null} */ (this.#q('[data-caption]'))?.value.trim() || '';
    const style = this.dataset.productTitle || '';

    for (const [i, s] of this.#bundleSlots().entries()) {
      const v = this.#variant(s.fit, s.size);
      if (!v) continue;
      const props = { Style: style, 'Bundle item': `${i + 2} of ${n}` };
      if (s.what === 'same') {
        Object.assign(props, { 'Pet name': name, Sex: sex, Caption: caption, Photo: 'Same photo as item 1', '_Photo delivery': 'Same as item 1' });
      } else if (s.what === 'other') {
        props['Portrait'] = 'Another pet or design — we email you after checkout';
      } else {
        props['Portrait'] = 'Choose later (store credit, 6 months)';
      }
      lines.push({ id: v.id, quantity: 1, properties: props });
    }

    for (const cb of this.querySelectorAll('[data-addon]:checked')) {
      lines.push({ id: Number(cb.getAttribute('data-addon')), quantity: 1 });
    }
    return lines;
  }

  // ---------- rendering ----------

  #render() {
    const st = this.#state;
    const v = this.#variant();

    // hidden variant id
    const idInput = this.#q('[data-variant-id]');
    if (idInput instanceof HTMLInputElement && v) idInput.value = String(v.id);

    // fit, colour, size controls
    for (const b of this.querySelectorAll('[data-fit]')) b.setAttribute('aria-pressed', String(st.fitChosen && b.getAttribute('data-fit') === st.fit));
    for (const b of this.querySelectorAll('[data-colour]')) b.setAttribute('aria-pressed', String(b.getAttribute('data-colour') === st.colour));
    const sizeGroup = this.#q('[data-size-group]');
    if (sizeGroup) {
      sizeGroup.innerHTML = st.fitChosen
        ? this.#sizesFor(st.fit)
            .map((s) => `<button type="button" class="chip" data-size="${s}" aria-pressed="${s === st.size}">${s}</button>`)
            .join('')
        : '<span class="sizes-wait">Pick the garment above – then the sizes appear.</span>';
    }
    // 2026-10-22 (ARVI): tees run small – a tip under the chips, worded for the size the shopper is looking at
    const tip = this.#q('[data-sizetip]');
    if (tip instanceof HTMLElement) tip.textContent = st.fitChosen ? this.#sizeTip(st.fit, st.size, this.#sizesFor(st.fit)) : '';
    // hom-position.js draws the garment in the size the shopper picked (until then the catalogue size)
    const shown = st.fitChosen && st.sizeChosen ? st.size : '';
    if ((this.dataset.sizeShown || '') !== shown) this.dataset.sizeShown = shown;

    // price + total
    const unit = v ? v.price : 0;
    const priceEl = this.#q('[data-price]');
    if (priceEl) priceEl.textContent = this.#money(unit);

    // gallery: front · back · worn (only the chosen cut and colour)
    const slides = this.#slides();
    const cur = slides.find((x) => x.view === st.view) || slides[0];
    if (cur && cur.view !== st.view) st.view = cur.view;
    const main = this.#q('[data-main-img]');
    if (main instanceof HTMLImageElement && cur) {
      if (main.getAttribute('src') !== cur.src) {
        main.src = cur.src;
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
      }
      main.alt = cur.alt;
    }
    const thumbs = this.#q('[data-thumbs]');
    if (thumbs) {
      const html = slides
        .map((x) => `<button type="button" data-thumb="${x.view}" aria-pressed="${x.view === st.view}" aria-label="${x.label}"><img src="${x.thumb}" alt="" loading="lazy" width="64" height="80"></button>`)
        .join('');
      if (thumbs.innerHTML !== html) thumbs.innerHTML = html;
    }

    // cross-sell image: the women's cut
    const xs = this.#q('[data-xsell-img]');
    const wv = this.#variants.find((x) => x.fit === 'Women');
    const wm = this.#media.find((x) => x.id === wv?.media);
    if (xs instanceof HTMLImageElement && wm && xs.getAttribute('src') !== wm.thumb.replace('width=160', 'width=300')) {
      xs.src = wm.thumb.replace('width=160', 'width=300');
    }

    // bundle tiles + slots (page B: the 1-item tile shows the price of the garment picked)
    for (const t of this.querySelectorAll('[data-bundle]')) t.setAttribute('aria-pressed', String(Number(t.getAttribute('data-bundle')) === st.bundle));
    const oneItem = this.#q('[data-bundle-sum]');
    const unitNow = this.#unitPrice(st.fit);
    if (oneItem && unitNow) oneItem.textContent = this.#money(unitNow);
    this.#renderSlots();

    // cur labels
    const curGarment = this.#q('[data-cur="garment"]');
    if (curGarment) curGarment.textContent = st.fitChosen ? `${FIT_LABEL[st.fit] || st.fit} · ${st.colour}` : 'Choose';
    const curBundle = this.#q('[data-cur="bundle"]');
    if (curBundle) curBundle.textContent = `${st.bundle} item${st.bundle > 1 ? 's' : ''}`;

    this.#renderSummary();
  }

  #renderSlots() {
    const el = this.#q('[data-slots]');
    if (!el) return;
    const n = this.#state.bundle;
    if (n < 2) {
      el.innerHTML = '';
      return;
    }
    const fits = [...new Set(this.#variants.map((v) => v.fit))];
    const label = (f) => `${FIT_LABEL[f] || f} ${this.#money(this.#unitPrice(f))}`;
    const first = `<div class="slot"><b>Item 1</b><span>${this.#state.fitChosen ? FIT_LABEL[this.#state.fit] || this.#state.fit : 'garment – pick above'} · this portrait · ${this.#money(this.#unitPrice(this.#state.fit))}</span></div>`;
    const rest = this.#slots
      .slice(0, n - 1)
      .map((s, i) => {
        const sizes = this.#sizesFor(s.fit);
        if (!sizes.includes(s.size)) s.size = sizes.includes(DEFAULT_SIZE[s.fit]) ? DEFAULT_SIZE[s.fit] : sizes[0];
        return `<div class="slot" data-slot="${i}"><b>Item ${i + 2}</b>
          <div class="seg small" role="group" aria-label="Item ${i + 2}: who wears it">${fits.map((f) => `<button type="button" data-slot-fit="${f}" aria-pressed="${s.fit === f}">${label(f)}</button>`).join('')}</div>
          <div class="seg small" role="group" aria-label="Item ${i + 2}: portrait">${[
            ['same', 'Same portrait'],
            ['other', 'Other pet / design'],
            ['later', 'Choose later (credit)'],
          ]
            .map(([k, l]) => `<button type="button" data-slot-what="${k}" aria-pressed="${s.what === k}">${l}</button>`)
            .join('')}</div>
          <div class="chips sizes onerow" role="group" aria-label="Item ${i + 2}: size">${sizes.map((z) => `<button type="button" class="chip" data-slot-size="${z}" aria-pressed="${s.size === z}">${z}</button>`).join('')}</div>
        </div>`;
      })
      .join('');
    el.innerHTML = first + rest;
  }

  #total() {
    const st = this.#state;
    let sum = this.#unitPrice(st.fit);
    for (const s of this.#bundleSlots()) sum += this.#unitPrice(s.fit);
    sum = Math.round(sum * (1 - this.#discount(st.bundle) / 100));
    for (const cb of this.querySelectorAll('[data-addon]:checked')) sum += Number(cb.getAttribute('data-addon-price')) || 0;
    return sum;
  }

  #renderSummary() {
    const st = this.#state;
    if (this.#needDesign) this.#showDesignNote();
    const total = this.#money(this.#total());
    const totalEl = this.#q('[data-total]');
    if (totalEl) totalEl.textContent = total;

    const name = /** @type {HTMLInputElement | null} */ (this.#q('[data-name]'))?.value.trim() || '—';
    const sex = /** @type {HTMLInputElement | null} */ (this.#q('[data-sex]:checked'))?.value || 'Male';
    const capInput = /** @type {HTMLInputElement | null} */ (this.#q('[data-caption]'));
    const cap = capInput?.value.trim() || '';
    for (const curName of this.querySelectorAll('[data-cur="name"]')) curName.textContent = name === '—' ? 'Not added yet' : `${name} · ${sex === 'Female' ? 'She' : 'He'}`;
    const capPrev = this.#q('[data-cap-preview]');
    if (capPrev) capPrev.textContent = cap || (capInput?.dataset.example ?? '');
    this.#renderPj(name === '—' ? '' : name, sex, cap);

    const sum = this.#q('[data-summary]');
    if (sum) {
      const garment = st.fitChosen ? `${FIT_CUT[st.fit] || st.fit} · ${st.colour} · ${st.size}` : 'who wears it: not chosen yet';
      sum.textContent = `In the cart: ${this.dataset.productTitle} · ${name} (${sex}) · caption „${cap || 'none'}“ · ${garment} · ${st.bundle}× · ${total}`;
    }

    const stickyName = this.#q('[data-sticky-name]');
    if (stickyName) stickyName.textContent = `${this.dataset.productTitle} · ${name} · ${st.colour} · ${st.size}`;
    const stickyTotal = this.#q('[data-sticky-total]');
    if (stickyTotal) stickyTotal.textContent = `${total} · ${st.bundle} ${st.bundle > 1 ? 'items' : 'item'}`;
    const stickyImg = this.#q('[data-sticky-img]');
    const m = this.#media[this.#frontIndex()];
    if (stickyImg instanceof HTMLImageElement && m && stickyImg.src !== m.thumb) stickyImg.src = m.thumb;
  }

  #pjFile = null;
  #pjUrl = '';

  /** Popup flow: what the studio already has (photo, name) as a small card above the button. */
  #renderPj(name, sex, cap) {
    const done = this.#q('[data-pj-done]');
    if (!(done instanceof HTMLElement)) return;
    const file = /** @type {HTMLInputElement | null} */ (this.#q('[data-photo-input]'));
    const hasPhoto = Boolean(file?.files?.length);
    const later = this.#state.photo === 'later';
    done.hidden = !(name || hasPhoto);
    const thumb = this.#q('[data-pj-thumb]');
    if (thumb instanceof HTMLImageElement) {
      const f = hasPhoto ? file?.files?.[0] : null;
      const showable = Boolean(f && /image\/(jpeg|png|webp)/.test(f.type));
      if (showable && this.#pjFile !== f) {
        if (this.#pjUrl) URL.revokeObjectURL(this.#pjUrl);
        this.#pjFile = f;
        this.#pjUrl = URL.createObjectURL(/** @type {File} */ (f));
        thumb.src = this.#pjUrl;
      }
      thumb.hidden = !showable;
    }
    const n = this.#q('[data-pj-name]');
    if (n) n.textContent = name ? `${name} · ${sex === 'Female' ? 'she' : 'he'}` : 'Name still missing';
    const note = this.#q('[data-pj-note]');
    if (note) note.textContent = [hasPhoto ? 'Photo added' : later ? 'Photo by email later' : 'Photo still missing', cap ? `„${cap}“` : ''].filter(Boolean).join(' · ');
    const cta = this.#q('[data-pj-cta]');
    if (cta instanceof HTMLElement) {
      const pet = this.dataset.pet || 'pet';
      cta.textContent = name && (hasPhoto || later) ? `Draw ${name} on this design →` : `See it with your ${pet} →`;
    }
  }

  #renderDates() {
    const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const addW = (d, n) => {
      d = new Date(d);
      while (n > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() % 6) n--;
      }
      return d;
    };
    const now = new Date();
    const cutoff = Number(this.dataset.cutoffHour) || 12;
    const start = now.getHours() >= cutoff ? addW(now, 1) : now;
    const p = Number(this.dataset.previewDays) || 1;
    const f = Number(this.dataset.deliveryFrom) || 6;
    const t = Number(this.dataset.deliveryTo) || 8;
    const set = (sel, txt) => {
      for (const el of this.querySelectorAll(sel)) el.textContent = txt;
    };
    set('[data-d0]', fmt(now));
    set('[data-d1], [data-d1b]', fmt(addW(start, p)));
    set('[data-d2], [data-d2b]', `${fmt(addW(start, f))}–${fmt(addW(start, t))}`);
  }

  /** A design without its own product arrives as ?style=<name>: record it on the order. */
  #applyStyleParam() {
    // "Make it yourself" from the home page arrives as ?idea=<text>: fill and open the field.
    const idea = (new URLSearchParams(window.location.search).get('idea') ?? '').replace(/[<>]/g, '').trim().slice(0, 300);
    const ideaField = this.querySelector('textarea[name="properties[Your idea]"]');
    if (idea && ideaField instanceof HTMLTextAreaElement) {
      ideaField.value = idea;
      const box = ideaField.closest('details');
      if (box) box.open = true;
    }
    const raw = new URLSearchParams(window.location.search).get('style');
    if (!raw) return;
    const style = raw.replace(/[<>]/g, '').trim().slice(0, 60);
    if (!style) return;
    const input = this.#q('[data-style-input]');
    if (input instanceof HTMLInputElement) input.value = style;
    this.dataset.productTitle = style;
    // the generic tee stands in for designs without their own product: show the design's name
    // and collection, not "The Tee" and whatever collection came first (ARVI 2026-10-12)
    const h1 = this.querySelector('h1');
    if (h1 && style !== 'Make it yourself') h1.textContent = style;
    const titleEl = this.#q('[data-crumb-title]');
    if (titleEl) titleEl.textContent = style;
    const COLS = { 'the-studio': 'The Studio', 'the-estate': 'The Estate', 'the-characters': 'The Characters', 'humanized-pets': 'Humanized Pets' };
    const col = (new URLSearchParams(window.location.search).get('col') ?? '').replace(/[^a-z-]/g, '');
    const crumb = this.#q('[data-crumb-col]');
    if (crumb instanceof HTMLAnchorElement) {
      crumb.href = col && COLS[col] ? `/collections/${col}` : '/collections/all-styles';
      crumb.textContent = col && COLS[col] ? COLS[col] : 'Styles';
    }
    document.title = `${style} · House of Morka`;
  }

  // ---------- photo ----------

  #setPhotoMode(mode) {
    this.#state.photo = mode;
    const later = mode === 'later';
    for (const b of this.querySelectorAll('[data-photo-mode]')) b.setAttribute('aria-pressed', String(b.getAttribute('data-photo-mode') === mode));
    const now = this.#q('[data-photo-now]');
    const hint = this.#q('[data-photo-later]');
    if (now instanceof HTMLElement) now.hidden = later;
    if (hint instanceof HTMLElement) hint.hidden = !later;
    const file = this.#q('[data-photo-input]');
    if (file instanceof HTMLInputElement) {
      file.required = !later;
      file.disabled = later;
    }
    const delivery = this.#q('[data-photo-delivery]');
    if (delivery instanceof HTMLInputElement) delivery.value = later ? 'Will email the photo later' : 'Uploaded now';
    const cur = this.#q('[data-cur="photo"]');
    if (cur) cur.textContent = later ? 'Send later' : 'Now';
  }

  /** @param {HTMLInputElement} input */
  #onPhotoChange(input) {
    const file = input.files?.[0];
    const box = this.#q('[data-upload]');
    const name = this.#q('[data-photo-name]');
    const hint = this.#q('[data-photo-hint]');
    const btn = this.#q('[data-photo-btn]');
    const img = this.#q('[data-photo-preview]');
    const ph = this.#q('[data-photo-ph]');
    const max = Number(this.dataset.maxFileSize) || 0;

    if (!file) {
      input.setCustomValidity('');
      box?.classList.remove('is-invalid');
      if (name) name.textContent = 'Choose a photo';
      if (hint) hint.textContent = 'JPG, PNG or HEIC, up to 20 MB. A phone photo is fine.';
      if (btn) btn.textContent = 'Choose';
      if (img instanceof HTMLElement) img.hidden = true;
      if (ph instanceof HTMLElement) ph.hidden = false;
      return;
    }

    const tooLarge = max > 0 && file.size > max;
    input.setCustomValidity(tooLarge ? 'The photo is larger than 20 MB. Please choose a smaller one.' : '');
    box?.classList.toggle('is-invalid', tooLarge);
    if (name) name.textContent = file.name;
    if (hint) hint.textContent = tooLarge ? 'Too large (max 20 MB). Please choose a smaller photo.' : "Looks good. We'll check it too.";
    if (btn) btn.textContent = 'Change';
    if (tooLarge) input.reportValidity();
    else this.#shrink(input, file);

    if (img instanceof HTMLImageElement && /image\/(jpeg|png|webp)/.test(file.type)) {
      const url = URL.createObjectURL(file);
      img.onload = () => URL.revokeObjectURL(url);
      img.src = url;
      img.hidden = false;
      if (ph instanceof HTMLElement) ph.hidden = true;
    } else if (img instanceof HTMLElement) {
      img.hidden = true;
      if (ph instanceof HTMLElement) ph.hidden = false;
    }
    this.#renderSummary();
  }

  /**
   * Phone photos are 3–12 MB and ride along with "Add to cart" – that was the 4–5 s wait.
   * Re-encode to max 2400 px JPEG in the browser (plenty for the portrait; HEIC the browser
   * can't decode stays as is).
   * @param {HTMLInputElement} input @param {File} file
   */
  async #shrink(input, file) {
    if (file.size < 1_500_000 || typeof createImageBitmap !== 'function' || typeof DataTransfer === 'undefined') return;
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const k = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
      const c = document.createElement('canvas');
      c.width = Math.round(bmp.width * k);
      c.height = Math.round(bmp.height * k);
      c.getContext('2d')?.drawImage(bmp, 0, 0, c.width, c.height);
      bmp.close();
      const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.9));
      if (!blob || blob.size >= file.size || input.files?.[0] !== file) return;
      const dt = new DataTransfer();
      dt.items.add(new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified }));
      input.files = dt.files;
    } catch {
      /* keep the original */
    }
  }

  // ---------- misc ----------

  #addingNow = false;
  #addingTimer = 0;

  /** Instant feedback: the photo travels with the order, so adding can take a few seconds. */
  #adding(on) {
    this.#addingNow = on;
    clearTimeout(this.#addingTimer);
    for (const b of this.querySelectorAll('[data-cta], [data-sticky-cta]')) {
      if (!(b instanceof HTMLButtonElement)) continue;
      if (on) {
        if (b.dataset.label === undefined) b.dataset.label = b.innerHTML;
        b.innerHTML = '<span class="cta-spin" aria-hidden="true"></span>Adding to your cart…';
        b.setAttribute('aria-busy', 'true');
        b.classList.add('is-adding');
      } else if (b.dataset.label !== undefined) {
        b.innerHTML = b.dataset.label;
        delete b.dataset.label;
        b.removeAttribute('aria-busy');
        b.classList.remove('is-adding');
      }
    }
    if (on) this.#addingTimer = window.setTimeout(() => this.#adding(false), 30000);
    else this.#renderSummary();
  }

  #setBusy(busy) {
    for (const b of this.querySelectorAll('[data-cta], [data-sticky-cta]')) {
      if (b instanceof HTMLButtonElement) b.disabled = busy;
    }
  }

  #showError(msg) {
    const el = this.#q('[data-form-error]');
    if (el instanceof HTMLElement) {
      el.textContent = msg;
      el.hidden = false;
      setTimeout(() => (el.hidden = true), 6000);
    }
  }

  #setupSticky() {
    const bar = this.#q('[data-sticky]');
    const cta = this.#q('[data-cta]');
    if (!bar || !cta || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) bar.classList.toggle('show', !en.isIntersecting && en.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    io.observe(cta);
    this.#ac.signal.addEventListener('abort', () => io.disconnect());
  }
}

if (!customElements.get('hom-product')) {
  customElements.define('hom-product', HomProduct);
}
